import { useEffect, useMemo, useRef, useState } from 'react';
import { Group, Shape } from 'react-konva';
import type Konva from 'konva';
import {
  getInterpolatedSprites,
  getVisualStyle,
  setRenderedByProductType,
  subscribeLiveFrames,
  type InterpolatedSprite,
  type LiveProductSprite,
} from '../../lib/liveFrameAnimation';
import {
  canvasProductSize,
  DEFAULT_PRODUCT_COLOR,
  DEFAULT_PRODUCT_SHAPE,
  drawProductShape,
  resolveProductVisual,
} from '../../lib/productVisualGeometry';
import {
  buildRouteGeometryCacheKey,
  getCachedRoutePoints,
  pointAlongPolyline,
  simProgressToDisplayPolylineT,
} from '../../lib/routeGeometry';
import type { ProjectNode, ProjectRoute } from '../../types/project';

// ------------------------------------------------------------
// Batched live simulation product sprites (single Shape draw pass)
// ------------------------------------------------------------

/** Server-side cap; client renders the full sampled frame without re-downsampling. */
export const SPRITE_RENDER_BUDGET = 5000;

export interface ActiveProductVisual {
  product_id?: number;
  route_id?: string;
  processor_id?: string;
  product_type_id?: string;
  color?: string;
  shape?: string;
  size_px?: number;
  progress?: number;
}

interface Props {
  sprites?: LiveProductSprite[];
  routes: ProjectRoute[];
  nodesById: Map<string, ProjectNode>;
  routeDisplayOffsets: Map<string, number>;
  nodeDisplayScale: number;
  zoom: number;
  pixelsPerMeter?: number;
  visible: boolean;
}

function resolveVisualStyleId(sprite: LiveProductSprite): string {
  return sprite.visual_style_id ?? sprite.product_type_id;
}

function resolvedSpriteStyle(
  styleId: string,
  spriteSizePx?: number,
) {
  const catalog = getVisualStyle(styleId);
  return resolveProductVisual({
    color: catalog?.color ?? DEFAULT_PRODUCT_COLOR,
    shape: catalog?.shape ?? DEFAULT_PRODUCT_SHAPE,
    size_px: spriteSizePx ?? catalog?.size_px,
  });
}

export default function ProductSpritesLayer({
  sprites = [],
  routes,
  nodesById,
  routeDisplayOffsets,
  nodeDisplayScale,
  zoom,
  pixelsPerMeter = 0,
  visible,
}: Props) {
  const shapeRef = useRef<Konva.Shape>(null);
  const [, setTick] = useState(0);

  useEffect(() => subscribeLiveFrames(() => setTick((n) => n + 1)), []);

  const routePointsById = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const route of routes) {
      const key = buildRouteGeometryCacheKey(
        route,
        nodesById,
        routeDisplayOffsets.get(route.id) ?? 0,
        nodeDisplayScale,
        zoom,
      );
      map.set(
        route.id,
        getCachedRoutePoints(
          key,
          route,
          nodesById,
          routeDisplayOffsets.get(route.id) ?? 0,
          nodeDisplayScale,
          zoom,
        ),
      );
    }
    return map;
  }, [routes, nodesById, routeDisplayOffsets, nodeDisplayScale, zoom]);

  const placements = useMemo(() => {
    if (!visible) return [];

    const interpolated: InterpolatedSprite[] =
      sprites.length > 0
        ? sprites.map((s) => ({
            ...s,
            interpolated_progress: s.progress ?? 0,
          }))
        : getInterpolatedSprites();
    const routeById = new Map(routes.map((route) => [route.id, route]));
    const processorCounts = new Map<string, number>();

    const result: Array<{
      key: string;
      x: number;
      y: number;
      styleId: string;
      sizePx?: number;
      progress?: number;
      opacity: number;
    }> = [];

    for (const sprite of interpolated) {
      const styleId = resolveVisualStyleId(sprite);

      if (sprite.route_id) {
        const route = routeById.get(sprite.route_id);
        if (!route) continue;
        const points = routePointsById.get(sprite.route_id) ?? [];
        const progress = sprite.interpolated_progress;
        const t =
          typeof progress === 'number' && Number.isFinite(progress)
            ? simProgressToDisplayPolylineT(
                progress,
                route,
                nodesById,
                nodeDisplayScale,
                zoom,
                pixelsPerMeter,
              )
            : 0.5;
        const { x, y } = pointAlongPolyline(points, t);
        result.push({
          key: `p:${sprite.product_id}`,
          x,
          y,
          styleId,
          sizePx: sprite.size_px,
          progress,
          opacity: 1,
        });
        continue;
      }

      if (sprite.processor_id) {
        const node = nodesById.get(sprite.processor_id);
        if (!node) continue;
        const index = processorCounts.get(sprite.processor_id) ?? 0;
        processorCounts.set(sprite.processor_id, index + 1);
        const baseR = node.radius ?? 18;
        const z = zoom > 0 ? zoom : 1;
        const ringOffset = Math.max(6, baseR * 0.42 * (nodeDisplayScale / z));
        const angle = (index / Math.max(1, index + 1)) * Math.PI * 2;
        result.push({
          key: `p:${sprite.product_id}`,
          x: node.x + Math.cos(angle) * ringOffset,
          y: node.y + Math.sin(angle) * ringOffset,
          styleId,
          sizePx: sprite.size_px,
          opacity: 0.92,
        });
      }
    }

    const renderedCounts: Record<string, number> = {};
    for (const placement of result) {
      const sprite = interpolated.find(
        (item) => `p:${item.product_id}` === placement.key,
      );
      const typeId = sprite?.product_type_id;
      if (typeId) {
        renderedCounts[typeId] = (renderedCounts[typeId] ?? 0) + 1;
      }
    }
    setRenderedByProductType(renderedCounts);

    return result;
  }, [visible, sprites, routes, nodesById, routePointsById, nodeDisplayScale, zoom, pixelsPerMeter]);

  if (!visible || placements.length === 0) return null;

  return (
    <Group listening={false}>
      <Shape
        ref={shapeRef}
        perfectDrawEnabled={false}
        listening={false}
        sceneFunc={(ctx, konvaShape) => {
          for (const placement of placements) {
            const style = resolvedSpriteStyle(placement.styleId, placement.sizePx);
            const size = canvasProductSize(style.size_px, nodeDisplayScale, zoom);
            ctx.save();
            ctx.translate(placement.x, placement.y);
            drawProductShape(
              ctx,
              style.shape,
              size,
              style.color,
              placement.opacity,
            );
            ctx.restore();
          }
          ctx.fillStrokeShape(konvaShape);
        }}
      />
    </Group>
  );
}
